import React, { useEffect, useState } from "react";
import HelmetWrapper from "../../HelmetWrapper";
import { Col, Container, Row } from "react-bootstrap";
import PageHeadingNew from "../common-components/custom-comps/PageHeadingNew";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import TableSkeleton from "../common-components/TableSkeleton";
import { formatDateTime } from "../common-data/Converts";
import { useGetFetchNew } from "../../data/Queries";
import FilterContainer from "../common-components/filters/filter-container";
import DateRangerFilter from "../common-components/filters/date-ranger-filter";
import GymFilter from "../common-components/filters/gym-filter";
import "../../styles/lead-mangement/academy-leads.css";

const FitnessCoachLeadsStructure = ({ userInfoData }) => {
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [gymFilter, setGymFilter] = useState(null);

  // Build filters object
  const buildFilters = () => {
    const filters = {
      page: isPage + 1,
      limit: pageLimit,
    };

    // Add search term if provided
    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }

    // Add date range filters if provided
    if (startDate) {
      filters.date_from = startDate;
    }
    if (endDate) {
      filters.date_to = endDate;
    }

    // Add gym filter if provided
    if (gymFilter && gymFilter.value) {
      filters.gym_id = gymFilter.value;
    }

    return filters;
  };

  // Fetch fitness coach leads using useGetFetchNew hook
  const {
    data: fitnessCoachLeads,
    isLoading: fitnessCoachLeadsLoading,
    refetch,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "fitness_coach_leads",
    url: "/leads/fitness-coach-leads",
    filters: buildFilters(),
  });

  // Refetch when filters or pagination changes
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        setIsPage(0); // Reset to first page when filters change
        refetch();
      },
      searchTerm ? 300 : 0 // 300ms debounce for search, immediate for others
    );

    return () => clearTimeout(timeoutId);
  }, [searchTerm, startDate, endDate, gymFilter, refetch]);

  // Refetch when pagination changes
  useEffect(() => {
    refetch();
  }, [isPage, pageLimit, refetch]);

  const tbColumns = [
    {
      dataField: "name",
      text: "Coach Details",
      formatter: (_, row) => (
        <div className="emeberDetailsRowContainer">
          <span className="tb-data text-start">
            {row?.name || "NA"}
            <br />
            {(row?.mobile || row?.email) && (
              <small className="lowercase underline">
                {row?.mobile || ""}
                <br />
                {row?.email || ""}
              </small>
            )}
          </span>
        </div>
      ),
    },
    {
      dataField: "city",
      text: "City",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.city || "NA"}</span>;
      },
    },
    {
      dataField: "fitness_experience",
      text: "Experience Level",
      formatter: (_, row) => {
        return (
          <span className="tb-data capitalize">
            {row?.fitness_experience || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "description",
      text: "Description",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth200">
            {row?.description || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "gyms",
      text: "Preferred Gyms",
      formatter: (_, row) => {
        const gyms = row?.gyms || [];
        return (
          <span className="tb-data minWidth150">
            {gyms.length > 0 ? (
              <div>
                {gyms.slice(0, 2).map((gym, index) => (
                  <div key={index} className="small-text">
                    {gym.name}
                  </div>
                ))}
                {gyms.length > 2 && (
                  <small className="text-muted">+{gyms.length - 2} more</small>
                )}
              </div>
            ) : (
              "No preferences"
            )}
          </span>
        );
      },
    },
    {
      dataField: "cv",
      text: "CV",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.cv ? (
              <a
                href={row.cv}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary"
              >
                View CV
              </a>
            ) : (
              "Not uploaded"
            )}
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.date_added ? formatDateTime(row.date_added) : "NA"}
          </span>
        );
      },
    },
  ];

  // Handle filter changes
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Handle date range change
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setGymFilter(null);
    setIsPage(0);
  };

  // Render filters component
  const renderFilters = () => {
    return (
      <Row className="mb-4">
        <Col>
          <FilterContainer onReset={handleResetFilters}>
            {/* Search Filter */}
            <div className="filter-search-wrapper">
              <input
                type="text"
                placeholder="Search by coach name, mobile, or email..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="premium-search-input"
              />
            </div>

            {/* Date Range Filter */}
            <DateRangerFilter
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateRangeChange}
            />

            {/* Gym Filter */}
            <GymFilter selectedGym={gymFilter} setSelectedGym={setGymFilter} />
          </FilterContainer>
        </Col>
      </Row>
    );
  };

  console.log("fitnessCoachLeads", fitnessCoachLeads);

  return (
    <HelmetWrapper title="WTF: Fitness Coach Leads">
      <Container className="bg-cloud-k px-50px" fluid>
        <Row>
          <Col>
            <PageHeadingNew
              heading="Fitness Coach Leads"
              subheading="Manage Fitness Coach Applications"
            />
          </Col>
        </Row>

        {/* Filters Section */}
        {renderFilters()}

        <Row className="mt-4">
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mod-height-100vh">
              {fitnessCoachLeadsLoading ? (
                <TableSkeleton />
              ) : (
                <PaymentLinkTable
                  tableData={fitnessCoachLeads?.data || []}
                  tableColumns={tbColumns}
                  tablePagination={fitnessCoachLeads?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default FitnessCoachLeadsStructure;
